import { CONFERENCE_JOINED, CONFERENCE_LEFT } from '../base/conference';
import { PARTICIPANT_JOINED, PARTICIPANT_LEFT } from '../base/participants';
import { ReducerRegistry } from '../base/redux';

import {
    CLOSE_NOTES_MODAL,
    LOAD_CONFERENCE_NOTES,
    NOTES_PANE_CLOSE,
    NOTES_PANE_OPEN,
    NOTES_SAVE_STATUS,
    OPEN_NOTES_MODAL,
    SET_NOTES_SHARED_WITH,
    SET_NOTES_TEXT,
    SHARED_NOTES_DRAFT_CLEAR,
    SHARED_NOTES_DRAFT_UPDATE,
    SHARED_NOTES_EDIT_RESULT,
    SHARED_NOTES_EDIT_SUBMIT,
    SHARED_NOTES_RECEIVED,
    SHARED_NOTES_REMOVED,
    VIEW_SHARED_NOTES
} from './actionTypes';
import { EDIT_RESULT_ACCEPTED, EDIT_RESULT_FAILED, EDIT_RESULT_PENDING, REDUCER_KEY, SHARE_AUDIENCE_EVERYONE, SHARE_AUDIENCE_SELECTED, SHARE_PERMISSION_VIEW } from './constants';
import { mergeNotes } from './functions';

/**
 * Rebases our unsent draft onto a newer shared document, so the owner's edits
 * show up live and ours are kept. A draft the merge fully absorbed is done.
 *
 * @param {Object} sharedDrafts - Drafts by owner id, mutated.
 * @param {string} id - The owner id.
 * @param {Object} base - The document the draft was written against.
 * @param {Object} next - The newer shared document { text, richText, revision }.
 * @returns {void}
 */
function _rebaseDraft(sharedDrafts, id, base, next) {
    const draft = sharedDrafts[id];

    if (!draft) {
        return;
    }
    const merged = mergeNotes(base, draft, next);
    const opsOf = doc => JSON.stringify(doc.richText?.ops ?? [ { insert: `${doc.text}\n` } ]);

    if (opsOf(merged) === opsOf(next)) {
        delete sharedDrafts[id];
    } else {
        sharedDrafts[id] = { ...merged,
            baseRevision: next.revision };
    }
}

const DEFAULT_STATE = {
    isNotesModalOpen: false,
    isOpen: false,
    text: '',
    richText: null,
    roomName: null,
    saveStatus: 'saved',

    // Participant ids our notes are shared with. For the 'everyone' audience
    // this is the resolved list of current remote participants; joins append
    // and leaves remove, so late joiners are covered automatically.
    sharedWith: [],

    // 'selected' or 'everyone'.
    shareAudience: SHARE_AUDIENCE_SELECTED,

    // Per-recipient permission under the 'selected' audience: id -> 'view'|'edit'.
    sharePermissions: {},

    // Permission granted to everyone under the 'everyone' audience.
    everyonePermission: SHARE_PERMISSION_VIEW,

    // Epoch of our notes and share settings; edits and sharing changes bump it
    // so recipients can detect a stale base or a revoked grant.
    revision: 0,

    // Notes others share with us, by participant id:
    // { text, richText, permission, revision }.
    sharedNotes: {},

    // Pending/finished edit requests we sent, by owner id:
    // { requestId, revision, status }.
    editRequests: {},

    // Our unsent drafts of notes shared with us, by owner id:
    // { text, richText, baseRevision }. Kept in redux so tab switches, pane
    // unmounts and the second responsive instance share the same draft.
    sharedDrafts: {},

    // Whose shared notes the pane shows; null for our own.
    viewing: null
};

// Sharing state never survives a room switch: participant ids are per room.
const RESET_SHARING = {
    sharedWith: [],
    shareAudience: SHARE_AUDIENCE_SELECTED,
    sharePermissions: {},
    everyonePermission: SHARE_PERMISSION_VIEW,
    revision: 0,
    sharedNotes: {},
    editRequests: {},
    sharedDrafts: {},
    viewing: null
};

/**
 * Listen for actions that mutate the notes pane state.
 */
ReducerRegistry.register(
    REDUCER_KEY, (state = DEFAULT_STATE, action) => {
        switch (action.type) {
        case NOTES_PANE_CLOSE:
            return {
                ...state,
                isOpen: false
            };

        case NOTES_PANE_OPEN:
            return {
                ...state,
                isOpen: true
            };

        case OPEN_NOTES_MODAL:
            return {
                ...state,
                isNotesModalOpen: true
            };

        case CLOSE_NOTES_MODAL:
            return {
                ...state,
                isNotesModalOpen: false
            };

        case SET_NOTES_TEXT:
            return {
                ...state,
                text: action.text || '',
                richText: action.richText || null,
                saveStatus: 'saving',
                revision: state.revision + 1
            };

        case NOTES_SAVE_STATUS:
            return { ...state,
                saveStatus: action.saveStatus };

        case LOAD_CONFERENCE_NOTES:
            return {
                ...state,
                text: action.text || '',
                richText: action.richText || null,
                roomName: action.roomName,
                saveStatus: action.saveStatus
            };

        case SET_NOTES_SHARED_WITH: {
            const changed = action.audience !== state.shareAudience
                || action.everyonePermission !== state.everyonePermission
                || action.participantIds.length !== state.sharedWith.length
                || action.participantIds.some((id, index) => id !== state.sharedWith[index])
                || JSON.stringify(action.permissions) !== JSON.stringify(state.sharePermissions);

            return { ...state,
                sharedWith: action.participantIds,
                shareAudience: action.audience,
                sharePermissions: action.permissions,
                everyonePermission: action.everyonePermission,
                revision: changed ? state.revision + 1 : state.revision };
        }

        case SHARED_NOTES_RECEIVED: {
            const editRequests = { ...state.editRequests };
            const sharedDrafts = { ...state.sharedDrafts };
            const request = editRequests[action.participantId];
            const previous = state.sharedNotes[action.participantId];

            if (action.permission !== 'edit') {
                // A downgrade can no longer honour a pending request, but an
                // unsent draft stays available for copy/download.
                delete editRequests[action.participantId];
            } else if (request?.status === EDIT_RESULT_ACCEPTED
                    && Number.isSafeInteger(action.revision) && action.revision >= request.revision) {
                // The authoritative document containing our accepted edit
                // arrived — the draft and the resolved request are done.
                delete editRequests[action.participantId];
                _rebaseDraft(sharedDrafts, action.participantId, request, action);
            } else if (request?.status !== EDIT_RESULT_PENDING && request?.status !== EDIT_RESULT_ACCEPTED
                    && sharedDrafts[action.participantId]?.baseRevision === previous?.revision
                    && action.revision !== previous?.revision) {
                // No request in flight: follow the owner instead of going
                // stale. A resolved conflict is retried from the rebased draft.
                delete editRequests[action.participantId];
                _rebaseDraft(sharedDrafts, action.participantId, previous, action);
            }

            return { ...state,
                sharedNotes: { ...state.sharedNotes,
                    [action.participantId]: { text: action.text,
                        richText: action.richText,
                        permission: action.permission,
                        revision: action.revision ?? null } },
                editRequests,
                sharedDrafts };
        }

        case SHARED_NOTES_DRAFT_UPDATE:
            return { ...state,
                sharedDrafts: { ...state.sharedDrafts,
                    [action.participantId]: { text: action.text,
                        richText: action.richText ?? null,
                        baseRevision: action.baseRevision } } };

        case SHARED_NOTES_DRAFT_CLEAR: {
            // Clearing the draft also drops the resolved request so the next
            // edit submits as a fresh request (#conflict recovery).
            const sharedDrafts = { ...state.sharedDrafts };
            const editRequests = { ...state.editRequests };

            delete sharedDrafts[action.participantId];
            delete editRequests[action.participantId];

            return { ...state,
                sharedDrafts,
                editRequests };
        }

        case SHARED_NOTES_EDIT_SUBMIT:
            return { ...state,
                editRequests: { ...state.editRequests,
                    [action.participantId]: { requestId: action.requestId,
                        revision: action.revision,
                        richText: action.richText ?? null,
                        status: EDIT_RESULT_PENDING,
                        text: action.text } } };

        case SHARED_NOTES_EDIT_RESULT: {
            const request = state.editRequests[action.ownerId];
            const share = state.sharedNotes[action.ownerId];

            // Late or foreign acks must not touch a pending/resolved request.
            // The one exception: a request accepted whose authoritative share
            // never arrived may still time out into 'failed'.
            if (!request || request.requestId !== action.requestId
                    || (request.status !== EDIT_RESULT_PENDING
                        && !(request.status === EDIT_RESULT_ACCEPTED && action.status === EDIT_RESULT_FAILED))) {
                return state;
            }
            const resolved = { ...request,
                status: action.status,
                revision: action.revision ?? request.revision };
            const editRequests = { ...state.editRequests,
                [action.ownerId]: resolved };
            const sharedDrafts = { ...state.sharedDrafts };

            // When the authoritative document already contains the accepted
            // edit — the share arrived before the verdict — done right away.
            if (action.status === EDIT_RESULT_ACCEPTED
                    && Number.isSafeInteger(action.revision)
                    && Number.isSafeInteger(share?.revision)
                    && share.revision >= action.revision) {
                delete editRequests[action.ownerId];
                _rebaseDraft(sharedDrafts, action.ownerId, resolved, share);
            }

            return { ...state,
                editRequests,
                sharedDrafts };
        }

        // The owner stopped sharing their notes with us — inbound share only;
        // our outbound grant to them is unrelated and stays untouched.
        case SHARED_NOTES_REMOVED: {
            const id = action.participantId;
            const sharedNotes = { ...state.sharedNotes };
            const editRequests = { ...state.editRequests };
            const sharedDrafts = { ...state.sharedDrafts };

            delete sharedNotes[id];
            delete editRequests[id];
            delete sharedDrafts[id];

            return { ...state,
                sharedNotes,
                editRequests,
                sharedDrafts,
                viewing: state.viewing === id ? null : state.viewing };
        }

        case PARTICIPANT_LEFT: {
            const id = action.participantId ?? action.participant?.id;
            const sharedNotes = { ...state.sharedNotes };
            const editRequests = { ...state.editRequests };
            const sharedDrafts = { ...state.sharedDrafts };
            const sharePermissions = { ...state.sharePermissions };

            delete sharedNotes[id];
            delete editRequests[id];
            delete sharedDrafts[id];
            delete sharePermissions[id];

            return { ...state,
                sharedNotes,
                editRequests,
                sharedDrafts,
                sharePermissions,
                sharedWith: state.sharedWith.filter(sharedId => sharedId !== id),
                viewing: state.viewing === id ? null : state.viewing };
        }

        case PARTICIPANT_JOINED: {
            const { participant } = action;

            // 'Everyone' covers late joiners; they get the current share next.
            if (state.shareAudience !== SHARE_AUDIENCE_EVERYONE || !participant
                    || participant.local || participant.fakeParticipant
                    || state.sharedWith.includes(participant.id)) {
                return state;
            }

            return { ...state,
                sharedWith: [ ...state.sharedWith, participant.id ] };
        }

        case VIEW_SHARED_NOTES:
            return { ...state,
                viewing: action.participantId };

        // Participant ids are per room, so sharing never carries over a room switch.
        case CONFERENCE_JOINED:
        case CONFERENCE_LEFT:
            return { ...state,
                ...RESET_SHARING };

        default:
            return state;
        }
    }
);
